import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../../App";
import { mono, Chip, Card, PageTitle } from "./ptUi";

// My Day — the production unit's command center. Reads /api/ptracker/today
// (an aggregate over pt_* + the live pipeline). Lead can edit the daily review.

function Tile({ label, value, sub, tone, onClick }) {
  const color = tone === "red" ? "var(--r)" : tone === "amber" ? "#d97706" : "var(--t1)";
  return (
    <div onClick={onClick} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)",
      padding: 14, cursor: onClick ? "pointer" : "default", transition: "transform .15s" }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "none")}>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: "var(--t3)", letterSpacing: ".06em", textTransform: "uppercase", marginTop: 7 }}>{label}</div>
      {sub ? <div style={{ fontFamily: mono, fontSize: 10, color: "var(--t4)", marginTop: 3 }}>{sub}</div> : null}
    </div>
  );
}

export default function PtrackerMyDay() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [d, setD] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [focus, setFocus] = useState("");
  const [blocker, setBlocker] = useState("");
  const navigate = useNavigate();

  const load = (day) => {
    api.get(`/ptracker/today?d=${day}`)
      .then((r) => { setData(r.data); setFocus(r.data.review?.todays_focus || ""); setBlocker(r.data.review?.key_blocker || ""); })
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load My Day."));
  };
  useEffect(() => { load(d); }, [d]);

  const saveReview = (patch) => {
    if (!isLead) return;
    api.put(`/ptracker/reviews/${d}`, patch).then((r) => setData((x) => ({ ...x, review: r.data }))).catch(() => {});
  };
  const firstName = (user?.full_name || user?.name || "").split(" ")[0];
  const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1000, margin: "0 auto" }} data-testid="ptracker-myday">
      {/* Hero */}
      <div style={{ position: "relative", overflow: "hidden", borderRadius: "var(--radius-sm)", border: "1px solid var(--bdr)",
        background: "linear-gradient(135deg,var(--s2),var(--s1))", padding: 22, marginBottom: 18 }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--r)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "var(--font-heading, var(--font-orb))", fontSize: 26, fontWeight: 700, color: "var(--t1)", margin: 0, textTransform: "uppercase" }}>
              {greeting()}{firstName ? `, ${firstName}` : ""}
            </h1>
            <p style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", letterSpacing: ".06em", marginTop: 4, textTransform: "uppercase" }}>
              {d} · {data?.tasks_planned || 0} tasks · {data?.assets_in_flight || 0} assets in flight · {data?.open_risks || 0} open risks
            </p>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="date" value={d} onChange={(e) => setD(e.target.value || new Date().toISOString().slice(0, 10))}
              style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", color: "var(--t2)", fontFamily: mono, fontSize: 12, padding: "6px 8px" }} />
            <span onClick={() => saveReview({ ap_reviewed: !data?.review?.ap_reviewed })}
              style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, padding: "6px 10px", borderRadius: "var(--radius-sm)",
                cursor: isLead ? "pointer" : "default", background: data?.review?.ap_reviewed ? "rgba(16,163,12,.15)" : "var(--s1)",
                color: data?.review?.ap_reviewed ? "#0ca30c" : "var(--t3)", border: "1px solid var(--bdr)" }}>
              {data?.review?.ap_reviewed ? "✓ AP REVIEWED" : "AP REVIEW"}
            </span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
          {[["🎯 Today's focus", focus, setFocus, "todays_focus"], ["🚧 Key blocker", blocker, setBlocker, "key_blocker"]].map(([lbl, val, setter, key]) => (
            <div key={key}>
              <div style={{ fontFamily: mono, fontSize: 9, fontWeight: 700, color: "var(--t3)", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 4 }}>{lbl}</div>
              <input value={val} disabled={!isLead} onChange={(e) => setter(e.target.value)}
                onBlur={() => saveReview({ [key]: val })} placeholder={isLead ? "…" : "—"}
                style={{ width: "100%", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)",
                  color: "var(--t1)", fontFamily: "var(--font-sans)", fontSize: 13, padding: "8px 10px", boxSizing: "border-box" }} />
            </div>
          ))}
        </div>
      </div>

      {err && <Card style={{ color: "var(--r)", fontFamily: mono, fontSize: 12 }}>{err}</Card>}

      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
            <Tile label="Shoots today" value={(data.shoots_today || []).length} onClick={() => navigate("/ptracker/shoots")} />
            <Tile label="Shoots tomorrow" value={data.shoots_tomorrow || 0} onClick={() => navigate("/ptracker/shoots")} />
            <Tile label="Studios booked" value={data.studios_booked || 0} onClick={() => navigate("/ptracker/shoots")} />
            <Tile label="Studios free" value={data.studios_free || 0} sub={(data.studios_free_names || []).join(", ") || undefined} onClick={() => navigate("/ptracker/shoots")} />
            <Tile label="Pending handovers" value={data.pending_handovers || 0} tone={data.pending_handovers ? "amber" : null} onClick={() => navigate("/ptracker/shoots")} />
            <Tile label="Tasks planned" value={data.tasks_planned} onClick={() => navigate("/ptracker/tasks")} />
            <Tile label="Tasks delivered" value={data.tasks_delivered} onClick={() => navigate("/ptracker/tasks")} />
            <Tile label="Needs attention" value={(data.attention_tasks || []).length} tone={(data.attention_tasks || []).length ? "red" : null} onClick={() => navigate("/ptracker/tasks")} />
            <Tile label="Assets in flight" value={data.assets_in_flight} onClick={() => navigate("/ptracker/pipeline")} />
            <Tile label="Assets overdue" value={data.assets_overdue} tone={data.assets_overdue ? "red" : null} onClick={() => navigate("/ptracker/pipeline")} />
            <Tile label="Open high risks" value={(data.high_risks || []).length} tone={(data.high_risks || []).length ? "red" : null} onClick={() => navigate("/ptracker/risks")} />
            <Tile label="KPIs missing 7d+" value={data.kpi_backlog} tone={data.kpi_backlog ? "amber" : null} onClick={() => navigate("/ptracker/pipeline")} />
            <Tile label="Team out" value={(data.team_out || []).length} onClick={() => navigate("/ptracker/attendance")} />
          </div>

          {(data.shoots_today || []).length > 0 && (
            <>
              <h2 style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t3)", letterSpacing: ".12em", textTransform: "uppercase", marginTop: 22, marginBottom: 10 }}>Today's shoots</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.shoots_today.map((s) => (
                  <div key={s.id} onClick={() => navigate("/ptracker/shoots")} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: "10px 14px", cursor: "pointer" }}>
                    <span style={{ fontFamily: mono, fontSize: 16 }}>🎬</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)" }}>{s.brand_project}</div>
                      <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{s.studio_location} · {s.type || "Shoot"} · {s.director_name || "—"}</div>
                    </div>
                    <span style={{ fontFamily: mono, fontSize: 11, color: "var(--t2)", whiteSpace: "nowrap" }}>{(s.actual_call || s.planned_call || "—")}–{(s.actual_wrap || s.planned_wrap || "—")}</span>
                    <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: s.readiness_status === "Ready" ? "#0ca30c" : s.readiness_status === "Nearly" ? "#d97706" : "var(--r)" }}>{s.readiness_done}/8</span>
                    <Chip value={s.shoot_status} />
                  </div>
                ))}
              </div>
            </>
          )}

          <h2 style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t3)", letterSpacing: ".12em", textTransform: "uppercase", marginTop: 22, marginBottom: 10 }}>Needs attention</h2>
          {(data.attention_tasks || []).length === 0 && (data.high_risks || []).length === 0 ? (
            <Card style={{ textAlign: "center", fontFamily: mono, fontSize: 12, color: "var(--t3)" }}>Nothing blocked or high-risk. Smooth sailing ✨</Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(data.attention_tasks || []).map((t) => (
                <div key={t.id} onClick={() => navigate("/ptracker/tasks")} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: "10px 14px", cursor: "pointer" }}>
                  <span style={{ fontFamily: mono, fontSize: 9, fontWeight: 700, color: "var(--t3)", background: "var(--s2)", padding: "2px 6px", borderRadius: 3 }}>TASK</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)" }}>{t.planned_output || t.item_type}</div>
                    <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{t.owner_name} · {t.brand_project}{t.blocker_reason ? ` · ${t.blocker_reason}` : ""}</div>
                  </div>
                  <Chip value={t.workflow_status} />
                </div>
              ))}
              {(data.high_risks || []).map((r) => (
                <div key={r.id} onClick={() => navigate("/ptracker/risks")} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: "10px 14px", cursor: "pointer" }}>
                  <span style={{ fontFamily: mono, fontSize: 9, fontWeight: 700, color: "var(--r)", background: "var(--rd)", padding: "2px 6px", borderRadius: 3 }}>RISK</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)" }}>{r.description}</div>
                    <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{r.area} · {r.brand_project || "—"} · {r.owner_name}</div>
                  </div>
                  <Chip value={r.severity} />
                </div>
              ))}
            </div>
          )}

          {(data.team_out || []).length > 0 && (
            <>
              <h2 style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t3)", letterSpacing: ".12em", textTransform: "uppercase", marginTop: 22, marginBottom: 10 }}>Who's out</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {data.team_out.map((c) => (
                  <span key={c.id} style={{ fontFamily: mono, fontSize: 11, color: "var(--t2)", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 20, padding: "4px 12px" }}>
                    {c.member_name} · {c.mark === "A" ? "Absent" : "Leave"}
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
